export interface DrugFaqItem {
  q: string;
  a: string;
}

export interface DrugFaqInput {
  displayName: string;
  primaryBrand?: string | null;
  productCount: number;
  inStockCount: number;
  strengths: string[];
  dosageForms: string[];
  manufacturers: string[];
  teCodes: string[];
  deaSchedule?: string | null;
  mechanismOfAction?: string | null;
}

function explainTeCode(code: string): string {
  const prefix = code.slice(0, 2).toUpperCase();
  const explanations: Record<string, string> = {
    AA: "Therapeutically equivalent — conventional dosage form with no known bioequivalence issues.",
    AB: "Therapeutically equivalent — meets FDA bioequivalence requirements; may be substituted for the reference listed drug.",
    AN: "Therapeutically equivalent — aerosolization solution or powder.",
    AO: "Therapeutically equivalent — injectable oil solution.",
    AP: "Therapeutically equivalent — injectable aqueous solution.",
    AT: "Therapeutically equivalent — topical product.",
    BC: "Not therapeutically equivalent — extended-release form that does not meet bioequivalence standards.",
    BD: "Not therapeutically equivalent — documented bioequivalence problem with this active ingredient and dosage form.",
    BE: "Not therapeutically equivalent — delayed-release oral dosage form.",
    BN: "Not therapeutically equivalent — aerosol-nebulizer product.",
    BP: "Not therapeutically equivalent — potential bioequivalence problem.",
    BR: "Not therapeutically equivalent — suppository or enema.",
    BS: "Not therapeutically equivalent — drug standard deficiency.",
    BT: "Not therapeutically equivalent — topical product with documented bioequivalence issues.",
    BX: "Not therapeutically equivalent — insufficient data to determine equivalence.",
  };
  return explanations[prefix] ?? `TE code ${code} — consult the FDA Orange Book for details.`;
}

export function buildDrugFaq(input: DrugFaqInput): DrugFaqItem[] {
  const {
    displayName,
    primaryBrand,
    productCount,
    inStockCount,
    strengths,
    dosageForms,
    manufacturers,
    teCodes,
    deaSchedule,
    mechanismOfAction,
  } = input;

  const items: DrugFaqItem[] = [
    {
      q: `Where can I buy wholesale ${displayName}${primaryBrand ? ` (${primaryBrand})` : ""} for my pharmacy?`,
      a: `Premier Pharma stocks ${productCount} ${displayName} SKUs — ${inStockCount} currently in stock — available to licensed pharmacies, clinics, doctor's offices, and veterinary practices.${primaryBrand ? ` ${displayName} is the generic equivalent of ${primaryBrand}.` : ""} Create a free account to see live wholesale pricing and place orders with same-day fulfillment for in-stock items.`,
    },
    {
      q: `What strengths and dosage forms of ${displayName} are available?`,
      a: `Premier Pharma carries ${displayName} in ${strengths.length > 0 ? strengths.slice(0, 5).join(", ") : "multiple strengths"} and ${dosageForms.length > 0 ? dosageForms.join(", ") : "various dosage forms"}. ${manufacturers.length > 0 ? `Available from manufacturers including ${manufacturers.slice(0, 3).join(", ")}.` : ""}`,
    },
  ];

  if (teCodes.length > 0) {
    const teCode = teCodes[0];
    items.push({
      q: `What is the therapeutic equivalence (TE) rating for ${displayName}?`,
      a: `${displayName} carries an FDA Orange Book TE code of ${teCode}. ${explainTeCode(teCode)} Always verify substitution with the current Orange Book before dispensing.`,
    });
  }

  if (deaSchedule) {
    items.push({
      q: `Is ${displayName} a controlled substance?`,
      a: `${displayName} is classified as DEA Schedule ${deaSchedule}. Ordering and dispensing requirements vary by state; ensure your pharmacy's DEA registration covers this schedule.`,
    });
  }

  if (mechanismOfAction) {
    items.push({
      q: `How does ${displayName} work?`,
      a: mechanismOfAction.slice(0, 600),
    });
  }

  return items;
}

export function buildFaqPageJsonLd(items: DrugFaqItem[]) {
  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: items.map((item) => ({
      "@type": "Question",
      name: item.q,
      acceptedAnswer: {
        "@type": "Answer",
        text: item.a,
      },
    })),
  };
}